import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy
} from '@angular/router';

import {Logger} from "@/util/logger";

/**
 * helper to allow some routes to be reused even after you navigate away and back to them.
 *
 * ref: https://www.auroria.io/angular-route-reuse-strategy/
 *      https://www.agiratech.com/angular-reuse-strategy
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      July 2022
 *
 */
export class AppRouteReuseStrategy implements RouteReuseStrategy {

  /** the routes to reuse */
  private routesToCache: string[]                 = [
    "overwatch",
    ""
  ];

  /** the cached routes */
  private static storedRouteHandles: Map<string, DetachedRouteHandle> = null;

  constructor() {

    Logger.debug(`[AppRouteReuseStrategy] constructing anew...`);

    if (AppRouteReuseStrategy.storedRouteHandles === null) {

      Logger.debug(`[AppRouteReuseStrategy] . first time creation of route cache...`);

      AppRouteReuseStrategy.storedRouteHandles = new Map<string, DetachedRouteHandle>()
    }

  }

  /** clear the cache, say when the user logs out. */
  clearHandlers(): void{

    Logger.debug(`[AppRouteReuseStrategy] clearing cache... `);

    AppRouteReuseStrategy.storedRouteHandles.clear();

  }

  /**determine whether the component can be detached ( stored instead of destroying it) for re-use */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {

    let path: string    =  this.getPath(route);
    let cached: boolean = this.routesToCache.indexOf(path) > -1;
    let detach: boolean = !!this.routesToCache.find(x => x == path);

    Logger.debug(`[AppRouteReuseStrategy] detach - already cached (${cached}) for detach of: ${path}`);
    Logger.debug(`[AppRouteReuseStrategy] detach - '${path}' DETACH: ${detach ? "YES" : "NO"}`);

    /* we have to return true if this path is cacheable */

    return detach;
  }

  /** save the route for later */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle): void {

    let path: string =  this.getPath(route);

    Logger.debug(`[AppRouteReuseStrategy] SAVING route `, path);

    AppRouteReuseStrategy.storedRouteHandles.set(path, handle);
  }

  /** determine whether the component that we are trying to load should be re-used */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {

    let path: string    =  this.getPath(route);
    let cached: boolean = AppRouteReuseStrategy.storedRouteHandles.has(path);
    let attach: boolean = !!this.routesToCache.find(x => x == path);

    Logger.debug(`[AppRouteReuseStrategy] attach - already cached (${cached ? "YES" : "NO"}) for detach of: ${path}`);
    Logger.debug(`[AppRouteReuseStrategy] attach - '${path}' == ${attach ? "YES" : "NO"}`);

    /* we need to return true if we want to reuse what we have cached. */

    return attach && cached;
  }

  /** reload a previous route (called if shouldAttach returns true) */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle {

    let path: string    =  this.getPath(route);

    Logger.debug(`[AppRouteReuseStrategy] CACHED: '${path}'`);

    return AppRouteReuseStrategy.storedRouteHandles.get(path) as DetachedRouteHandle;
  }

  /** decide if we should reuse a route or not...normally just return true. */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {

    if (future.component && (<any>future.component).name == "ShowComponent" ) {

      return false;
    }

    let reuse: boolean = future.routeConfig === curr.routeConfig;

    let path: string   =  this.getPath(future);

    //let reuse: boolean = (future.routeConfig === curr.routeConfig) || future.data?.reuseComponent;

    Logger.debug(`[AppRouteReuseStrategy] reuse - '${path}' REUSE: ${reuse ? "YES" : "NO"}`);

    return reuse;
  }

  /** (helper) extract the path/uri for route */
  private getPath(route: ActivatedRouteSnapshot): string {

    let path: string = '';

    if ((route.routeConfig != null) && (route.routeConfig.path != null)) {

      path = route.routeConfig.path;
    }

    return path;
  }

}
